import type { FC, MouseEvent } from 'react';
import { useState } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';
import Button from '@components/Button';
import { SaveAsNewChildBlockModal } from '@ContractBuilder/contract-builder-footer/SaveAsNewChildBlockModal';
import { useBlockEditFormStore } from '@ContractBuilder/modules/block-edit';
import { UnsavedChangesModal } from '@ContractBuilder/modules/block-edit/views/UnsavedChangesModal';
import { useOnBlockEditSubmit } from '@features/inline-block-edit';
import { INLINE_BLOCK_EDITOR_NAME, useEditorsMap } from '@providers/editors-map';
import { blockQueries } from '@queries/fetchBlocks';
import Nav from '@root/src/routes';
import { isBlocksPath } from '@root/src/utils/app-paths';
import { useQueryClient } from '@tanstack/react-query';
import { isString } from 'lodash-es';

import { LibraryBlockActionButtons } from '../components/LibraryBlockActionButtons';
interface LibraryBlockEditFooterProps {
  hasChanges: boolean;
  onUpdate: () => Promise<any> | void;
}

export const LibraryBlockEditFooter: FC<LibraryBlockEditFooterProps> = ({ hasChanges, onUpdate }) => {
  const state = useBlockEditFormStore(({ formValues }) => formValues);
  const { onSubmit, onSubmitAsNew } = useOnBlockEditSubmit();
  const { getEditor } = useEditorsMap();
  const [isSaveAsNewModalOpen, setSaveAsNewModalOpen] = useState<boolean>(false);
  const [isSavingAsNew, setIsSavingAsNew] = useState<boolean>(false);
  const [isUnsavedChangesModalOpen, setIsUnsavedChangesModalOpen] = useState<boolean>(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isBlocks = isBlocksPath();

  const editor = getEditor(INLINE_BLOCK_EDITOR_NAME);

  const isSaveButtonDisabled = !editor;

  const handlePropagation = (event: MouseEvent) => {
    event.stopPropagation();
  };

  const handleOpenSaveAsNewModal = () => setSaveAsNewModalOpen(true);
  const handleCloseSaveAsNewModal = () => setSaveAsNewModalOpen(false);
  const handleConfirmSaveAsNew = async (name: string, isChildBlock: boolean) => {
    handleCloseSaveAsNewModal();

    try {
      setIsSavingAsNew(true);

      if (state) {
        const id = await onSubmitAsNew({ ...state, name }, isChildBlock);

        if (isString(id)) {
          await queryClient.invalidateQueries({ queryKey: blockQueries.list().queryKey });
          return navigate(generatePath(isBlocks ? Nav.Block : Nav.Clause, { id }), {
            replace: true,
          });
        }
      }
    } catch (_error) {
      /**/
    } finally {
      setIsSavingAsNew(false);
      setSaveAsNewModalOpen(false);
    }
  };

  const handleNavigateToListingPage = () => {
    return navigate(isBlocks ? Nav.Blocks : Nav.Clauses);
  };

  const handleSubmit = async () => {
    if (state) {
      const hasCompleted = await onSubmit(state);

      if (hasCompleted) {
        await onUpdate();
      }
    }
  };

  const handleCancel = () => {
    if (hasChanges) {
      return setIsUnsavedChangesModalOpen(true);
    }

    return handleNavigateToListingPage();
  };

  const handleDisregardChanges = () => {
    setIsUnsavedChangesModalOpen(false);
    return handleNavigateToListingPage();
  };

  const handleSaveChanges = async () => {
    await handleSubmit();
    setIsUnsavedChangesModalOpen(false);
    return handleNavigateToListingPage();
  };

  if (!editor) {
    return null;
  }

  return (
    <>
      <UnsavedChangesModal
        isOpen={isUnsavedChangesModalOpen}
        onCancel={() => setIsUnsavedChangesModalOpen(false)}
        onDisregardChanges={handleDisregardChanges}
        onSaveChanges={handleSaveChanges}
      />
      <div
        className="border-info-200! flex w-full items-center justify-between border-t pb-2 shadow-2xl"
        onClick={handlePropagation}
      >
        <div className="ml-auto flex w-full items-center justify-end py-2">
          <div className="mr-auto pl-4" data-testid="action-buttons">
            <LibraryBlockActionButtons onUpdate={onUpdate} hasChanges={hasChanges} />
          </div>
          <Button
            className="mr-4 h-8"
            kind="secondary"
            size="sm"
            isDisabled={isSaveButtonDisabled}
            onClick={handleCancel}
          >
            Cancel
          </Button>
          <Button
            className="mr-4 h-8"
            kind="primary"
            size="sm"
            isDisabled={isSaveButtonDisabled || isSavingAsNew || !hasChanges}
            onClick={handleSubmit}
          >
            Save
          </Button>
          <Button
            className="mr-4 h-8"
            kind="primary"
            size="sm"
            isDisabled={isSaveButtonDisabled || !hasChanges}
            loading={isSavingAsNew}
            onClick={handleOpenSaveAsNewModal}
          >
            Save as new
          </Button>
        </div>
      </div>
      <SaveAsNewChildBlockModal
        isLoading={isSavingAsNew}
        isOpen={isSaveAsNewModalOpen}
        onClose={handleCloseSaveAsNewModal}
        onConfirm={handleConfirmSaveAsNew}
      />
    </>
  );
};
